import React from "react";
import { Link } from "react-router-dom";

const LOGO_SRC = "/brand/site-logo.png";

function CbLogo({ size = 40 }) {
  return (
    <img
      src={LOGO_SRC}
      alt="ClippingBase"
      className="block shrink-0 object-contain"
      style={{ width: size, height: size }}
    />
  );
}

function XIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M18.244 2H21l-6.49 7.41L22 22h-6.75l-4.69-6.04L4.96 22H2.2l6.97-7.95L2 2h6.91l4.24 5.5L18.244 2Zm-1.18 18h1.86L7.05 4H5.1l11.964 16Z" />
    </svg>
  );
}
function DiscordIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.07.07 0 0 0-.074.035c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0c-.164-.39-.405-.875-.617-1.25a.077.077 0 0 0-.074-.035A19.74 19.74 0 0 0 3.687 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.292a.074.074 0 0 1 .077-.01c3.927 1.793 8.18 1.793 12.061 0a.074.074 0 0 1 .078.009c.12.1.246.198.373.292a.077.077 0 0 1-.006.128 12.298 12.298 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.994a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.548-13.66a.061.061 0 0 0-.031-.028ZM8.02 15.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.42-2.157 2.42Zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.42-2.157 2.42Z" />
    </svg>
  );
}
function InstagramIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="3" width="18" height="18" rx="5" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
    </svg>
  );
}

export function MarketingFooter() {
  return (
    <footer data-testid="cb-footer" className="bg-[#050505]">
      <div className="mx-auto max-w-7xl px-5 pt-14 pb-6 sm:px-8">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr]">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-2.5">
              <CbLogo size={36} />
              <span className="cb-display text-[18px] tracking-tight text-white">
                ClippingBase<span className="text-[#5a5a60]">.com</span>
              </span>
            </Link>
            <p className="mt-4 max-w-md text-sm text-[#8a8a90]">
              ClippingBase helps brands distribute short-form content at scale through a trusted creator network — delivering reach at a lower cost than traditional ads.
            </p>
          </div>

          {/* Right: socials + platform list, side-by-side, never stacked */}
          <div className="flex items-start justify-between gap-6">
            {/* Socials column */}
            <div className="flex flex-col gap-3">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">Socials</div>
              <div className="flex flex-col items-start gap-2.5">
                <a
                  href="https://x.com/clippingbase?s=21"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cb-footer-x"
                  aria-label="X (Twitter)"
                  className="group flex h-10 w-10 items-center justify-center rounded-full border border-[#1f1f22] text-zinc-400 transition-all hover:border-white/60 hover:text-white"
                >
                  <XIcon />
                </a>
                <a
                  href="https://discord.gg/8rhhsY8aHb"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cb-footer-discord"
                  aria-label="Discord"
                  className="group flex h-10 w-10 items-center justify-center rounded-full border border-[#1f1f22] text-zinc-400 transition-all hover:border-[#5865F2]/60 hover:text-[#5865F2]"
                >
                  <DiscordIcon />
                </a>
                <a
                  href="https://www.instagram.com/clippingbase.official/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="cb-footer-instagram"
                  aria-label="Instagram"
                  className="group flex h-10 w-10 items-center justify-center rounded-full border border-[#1f1f22] text-zinc-400 transition-all hover:border-[#E1306C]/60 hover:text-[#E1306C]"
                >
                  <InstagramIcon />
                </a>
              </div>
            </div>

            {/* Platform links column */}
            <div className="flex-1">
              <div className="text-xs font-bold uppercase tracking-[0.18em] text-zinc-500">Platform</div>
              <ul className="mt-4 space-y-2.5 text-sm text-zinc-300">
                <li>
                  <a href="https://form.typeform.com/to/yXF9HCS0" target="_blank" rel="noopener noreferrer" data-testid="cb-footer-brands" className="transition-colors hover:text-[#d8ff2b]">For Brands</a>
                </li>
                <li>
                  <Link to="/creator" data-testid="cb-footer-creators" className="transition-colors hover:text-[#d8ff2b]">For Creators</Link>
                </li>
                <li>
                  <Link to="/contact" data-testid="cb-footer-contact" className="transition-colors hover:text-[#d8ff2b]">Contact Us</Link>
                </li>
                <li>
                  <Link to="/privacy" data-testid="cb-footer-privacy" className="transition-colors hover:text-[#d8ff2b]">Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/terms" data-testid="cb-footer-terms" className="transition-colors hover:text-[#d8ff2b]">Terms of Service</Link>
                </li>
                <li>
                  <Link to="/data-deletion" data-testid="cb-footer-data-deletion" className="transition-colors hover:text-[#d8ff2b]">Data Deletion</Link>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom row */}
        <div className="mt-12 flex flex-col items-start justify-between gap-4 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-[#6a6a72]">© 2026 ClippingBase.com — All rights reserved.</p>
          <div className="flex items-center gap-2 text-xs text-[#8a8a90]">
            <span className="cb-status-dot inline-block h-2 w-2 rounded-full bg-emerald-500" />
            All Systems Operational
          </div>
        </div>
      </div>

      {/* Giant watermark */}
      <div className="cb-watermark-wrap mx-auto max-w-[100vw]">
        <div className="cb-watermark px-2">CLIPPINGBASE</div>
      </div>
    </footer>
  );
}
